import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const customAlertVariants = cva(
  "flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
  {
    variants: {
      variant: {
        success: "",
        warning: "",
        error: "",
        info: "",
      },
    },
    defaultVariants: {
      variant: "success",
    },
  },
);

const variantStyles = {
  success: {
    backgroundColor: "#EBFFF3",
    borderColor: "#8AFDB2",
    color: "#005B41",
  },
  warning: {
    backgroundColor: "#FFFBEB",
    borderColor: "#FDE68A",
    color: "#B45309",
  },
  error: {
    backgroundColor: "#FFEBEB",
    borderColor: "#FD8A8A",
    color: "#B40909",
  },
  info: {
    backgroundColor: "#E8DFFF",
    borderColor: "#C7B8FF",
    color: "#5D20DF",
  },
};

interface CustomAlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof customAlertVariants> {
  icon?: React.ReactNode;
}

const CustomAlert = React.forwardRef<HTMLDivElement, CustomAlertProps>(
  (
    { className, variant = "success", icon, children, style, ...props },
    ref,
  ) => {
    const currentVariant = variant || "success";

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          customAlertVariants({ variant: currentVariant }),
          className,
        )}
        style={{
          ...variantStyles[currentVariant as keyof typeof variantStyles],
          ...style,
        }}
        {...props}
      >
        {icon && <div className="flex-shrink-0 mt-0.5">{icon}</div>}
        <div className="flex-1">{children}</div>
      </div>
    );
  },
);
CustomAlert.displayName = "CustomAlert";

export { CustomAlert };
